import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Camera, Download, ExternalLink, FileText, GripVertical, Image as ImageIcon, LogOut, Plus, RotateCcw, Search, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Reticle } from './components/shared';
import { AgendaPanel, BrandingPanel, SponsorsPanel } from './components/AdminContent';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'cxo-admin-token';

const TABS = [
  { id: 'registrations', label: 'Registrations', endpoint: '/api/admin/registrations' },
  { id: 'winners', label: 'Winners', endpoint: '/api/admin/winners' },
  { id: 'partners', label: 'Partners', endpoint: '/api/admin/partners' },
  { id: 'files', label: 'Uploads', endpoint: '/api/admin/files' },
  { id: 'speakers', label: 'Speakers', endpoint: '/api/admin/speakers' },
  { id: 'branding', label: 'Logos & Branding', content: BrandingPanel },
  { id: 'sponsors', label: 'Sponsor Logos', content: SponsorsPanel },
  { id: 'agenda', label: 'Agenda', content: AgendaPanel },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const fmtSize = (bytes) => (bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`);

const inputCls =
  'mt-1.5 w-full rounded-lg border border-line/10 bg-ink/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition focus:border-lime/70 focus:ring-2 focus:ring-lime/15';

const cell = 'px-4 py-3 align-top text-[13px] text-slate-300';
const head = 'px-4 py-3 text-left font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500';

const speakerInitials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

function FileLink({ path, label }) {
  if (!path) return <span className="text-slate-600">—</span>;
  return (
    <a
      href={`${API}/api/files/${path}`}
      target="_blank"
      rel="noreferrer"
      data-testid={`file-link-${label || 'doc'}`}
      className="inline-flex items-center gap-1.5 text-lime transition-colors hover:underline"
    >
      <ExternalLink size={12} /> Open
    </a>
  );
}

function DeleteButton({ rowKey, onDelete, testid }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 3500);
    return () => clearTimeout(t);
  }, [confirming]);

  const click = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    try {
      await onDelete(rowKey);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <button
      data-testid={confirming ? `${testid}-confirm` : testid}
      onClick={click}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-all ${
        confirming
          ? 'border border-red-400/60 bg-red-400/15 text-red-300'
          : 'border border-line/10 text-slate-500 hover:border-red-400/60 hover:text-red-300'
      } disabled:opacity-50`}
    >
      <Trash2 size={11} /> {busy ? '…' : confirming ? 'Confirm?' : 'Delete'}
    </button>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      onLogin(res.data.user);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (err?.response?.data?.message || err?.message || 'Login failed. Please try again.');
      setError(msg);
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-lime/25 bg-ink-2 p-9 shadow-[0_0_80px_rgba(153,204,0,0.1)]"
      >
        <div className="flex items-center gap-3">
          <Reticle className="h-10 w-10" />
          <div>
            <p className="font-display text-lg font-extrabold tracking-tight">
              CXO <span className="text-lime">100</span>
            </p>
            <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-slate-500">Admin Console</p>
          </div>
        </div>
        <label className="mt-8 block">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">Email</span>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@cioaxis.com"
            className={inputCls}
          />
        </label>
        <label className="mt-4 block">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">Password</span>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={inputCls}
          />
        </label>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-lg border border-red-400/40 bg-red-400/10 px-4 py-2.5 text-xs text-red-300">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="mt-6 w-full rounded-full bg-lime py-3.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all hover:shadow-[0_0_36px_rgba(153,204,0,0.45)] disabled:opacity-60"
        >
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
        <a href="/" className="mt-5 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-lime">
          ← Back to site
        </a>
      </form>
    </div>
  );
}

function SpeakerForm({ auth, onAdded }) {
  const [form, setForm] = useState({ name: '', role: '', org: '', city: 'Both' });
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Photo too large (max 10 MB)');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', f);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/api/uploads`, fd);
      setPhoto(res.data);
      toast.success('Photo uploaded — now click Add Speaker');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Name and Designation are required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/speakers`,
        { name: form.name.trim(), role: form.role.trim(), org: form.org.trim(), city: form.city, photo_path: photo?.path || '' },
        auth()
      );
      toast.success('Speaker added to the home page');
      setForm({ name: '', role: '', org: '', city: 'Both' });
      setPhoto(null);
      onAdded();
    } catch (err) {
      if (err?.response?.status === 401) return;
      toast.error('Could not add speaker. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="speaker-form"
      className="mb-8 rounded-2xl border border-lime/25 bg-ink-2/70 p-6 md:p-8"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime">Add a speaker to the home page</p>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">Name <span className="text-lime">*</span></span>
          <input
            data-testid="admin-speaker-name-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Dr. Jane Doe"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">Designation <span className="text-lime">*</span></span>
          <input
            data-testid="admin-speaker-designation-input"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            placeholder="CISO"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">Organisation</span>
          <input
            data-testid="admin-speaker-org-input"
            value={form.org}
            onChange={(e) => setForm((f) => ({ ...f, org: e.target.value }))}
            placeholder="Company"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">City / Chapter</span>
          <select
            data-testid="admin-speaker-city-input"
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            className={inputCls}
          >
            <option value="Both">Both (Delhi &amp; Mumbai)</option>
            <option value="D">New Delhi</option>
            <option value="M">Mumbai</option>
          </select>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        {photo ? (
          <div className="flex items-center gap-3 rounded-lg border border-lime/40 bg-ink/60 p-2">
            <img src={`${API}${photo.url}`} alt="speaker" className="h-11 w-11 rounded-md border border-line/10 object-cover" />
            <span className="max-w-[160px] truncate text-xs text-slate-300">{photo.filename}</span>
            <button
              type="button"
              data-testid="admin-speaker-photo-remove"
              onClick={() => setPhoto(null)}
              aria-label="remove photo"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-line/10 text-slate-400 hover:border-lime/60 hover:text-lime"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <label
            className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-line/20 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-all hover:border-lime/50 hover:text-lime ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          >
            <Upload size={13} /> {uploading ? 'Uploading…' : 'Photo (optional)'}
            <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={pick} className="hidden" data-testid="admin-speaker-photo-input" />
          </label>
        )}
        <button
          type="submit"
          data-testid="admin-speaker-add-button"
          disabled={busy || uploading}
          className="inline-flex items-center gap-2 rounded-full bg-lime px-6 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-ink transition-all hover:shadow-[0_0_28px_rgba(153,204,0,0.45)] disabled:opacity-50"
        >
          <Plus size={14} /> {busy ? 'Adding…' : 'Add Speaker'}
        </button>
      </div>
    </form>
  );
}

function SpeakersTable({ rows, onDelete, onReorder, onAttachPhoto, auth, onRefresh }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [attaching, setAttaching] = useState(null);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('all');

  const getImgSrc = (path) => {
    if (!path) return null;
    if (path.startsWith('/img/') || path.startsWith('img/') || path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const filteredRows = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    const matchSearch =
      !q ||
      (r.name && r.name.toLowerCase().includes(q)) ||
      (r.role && r.role.toLowerCase().includes(q)) ||
      (r.org && r.org.toLowerCase().includes(q));
    const matchCity =
      cityFilter === 'all' ||
      !r.city ||
      r.city === 'Both' ||
      r.city.includes(cityFilter);
    return matchSearch && matchCity;
  });

  const clearAll = async () => {
    if (!window.confirm(`Are you sure you want to delete ALL ${rows.length} speakers? This will remove them from the home page.`)) return;
    try {
      await axios.delete(`${API}/api/admin/speakers/all`, auth());
      toast.success('All speakers deleted');
      onRefresh?.();
    } catch (err) {
      toast.error('Could not delete speakers');
    }
  };

  const restoreDefaults = async () => {
    if (!window.confirm('Restore 89 default speakers to the database?')) return;
    try {
      const res = await axios.post(`${API}/api/admin/speakers/reset-defaults`, {}, auth());
      toast.success(res.data?.message || 'Default speakers restored');
      onRefresh?.();
    } catch (err) {
      toast.error('Could not restore default speakers');
    }
  };

  const attach = async (key, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Photo too large (max 10 MB)');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', f);
    setAttaching(key);
    try {
      const res = await axios.post(`${API}/api/uploads`, fd);
      await onAttachPhoto(key, res.data.path);
      toast.success('Photo updated');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upload failed');
    } finally {
      setAttaching(null);
      e.target.value = '';
    }
  };

  const drop = (idx) => {
    if (dragIdx === null || dragIdx === idx) {
      setDragIdx(null);
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(null);
    onReorder(next);
  };

  return (
    <>
      {/* Top Filter and Action Bar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search speaker by name, company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-full border border-line/15 bg-ink/70 py-1.5 pl-9 pr-3 text-xs text-slate-200 placeholder:text-slate-500 focus:border-lime/60 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-1 rounded-full border border-line/10 bg-ink-2/60 p-1">
            {[['all', 'All'], ['D', 'Delhi'], ['M', 'Mumbai']].map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                onClick={() => setCityFilter(val)}
                className={`rounded-full px-3 py-1 font-mono text-[9px] uppercase tracking-wider transition-all ${
                  cityFilter === val ? 'bg-lime text-ink font-bold shadow-sm' : 'text-slate-400 hover:text-lime'
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {filteredRows.length} of {rows.length} speakers
          </span>
        </div>

        <div className="flex items-center gap-2">
          {rows.length === 0 ? (
            <button
              type="button"
              onClick={restoreDefaults}
              className="inline-flex items-center gap-1.5 rounded-full border border-lime/30 bg-lime/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest text-lime transition-all hover:bg-lime/20"
            >
              <RotateCcw size={12} /> Restore 89 Default Speakers
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={restoreDefaults}
                title="Restore default 89 speakers if missing"
                className="inline-flex items-center gap-1.5 rounded-full border border-line/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-400 transition-all hover:border-lime/40 hover:text-lime"
              >
                <RotateCcw size={11} /> Restore Defaults
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-500/20 bg-red-500/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-red-400 transition-all hover:bg-red-500/20"
              >
                <Trash2 size={11} /> Clear All Speakers
              </button>
            </>
          )}
        </div>
      </div>

      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        Drag rows to reorder — the home page follows this order
      </p>

      {!filteredRows.length ? (
        <p className="rounded-xl border border-line/10 bg-ink-2/50 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
          {rows.length ? 'No speakers matching your search' : 'No speakers in database yet'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line/10" data-testid="admin-table-speakers">
          <table className="w-full min-w-[760px]">
            <thead className="bg-ink-3/60">
              <tr>
                <th className={`${head} w-8`}></th>
                <th className={head}>Photo</th>
                <th className={head}>Name</th>
                <th className={head}>Designation</th>
                <th className={head}>Organisation</th>
                <th className={head}>City</th>
                <th className={head}>Added</th>
                <th className={`${head} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/5 bg-ink-2/40">
              {filteredRows.map((r, i) => (
                <tr
                  key={r._key || i}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => drop(i)}
                  onDragEnd={() => setDragIdx(null)}
                  data-testid={`admin-speaker-row-${i}`}
                  className={`transition-colors hover:bg-lime/5 ${dragIdx === i ? 'opacity-40' : ''}`}
                >
                  <td className={`${cell} w-8 cursor-grab text-slate-600`} data-testid={`admin-speaker-drag-${i}`}>
                    <GripVertical size={15} />
                  </td>
                  <td className={cell}>
                    <div className="flex items-center gap-3">
                      {r.photo_path ? (
                        <img
                          src={getImgSrc(r.photo_path)}
                          alt={r.name}
                          className="h-10 w-10 rounded-md border border-line/10 object-cover"
                        />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-md border border-line/10 bg-ink-3/70 font-display text-xs font-bold text-lime">
                          {speakerInitials(r.name)}
                        </span>
                      )}
                      <label
                        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-line/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500 transition-all hover:border-lime/60 hover:text-lime ${attaching === r._key ? 'pointer-events-none opacity-50' : ''}`}
                      >
                        <Camera size={11} /> {attaching === r._key ? '…' : r.photo_path ? 'Change' : 'Add photo'}
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          className="hidden"
                          data-testid={`admin-speaker-photo-attach-${i}`}
                          onChange={(e) => attach(r._key, e)}
                        />
                      </label>
                    </div>
                  </td>
                  <td className={`${cell} font-semibold text-slate-100`}>{r.name}</td>
                  <td className={cell}>{r.role}</td>
                  <td className={cell}>{r.org || '—'}</td>
                  <td className={cell}>
                    <span className="rounded-full bg-ink/70 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-lime border border-line/10">
                      {r.city === 'D' ? 'New Delhi' : r.city === 'M' ? 'Mumbai' : 'Both'}
                    </span>
                  </td>
                  <td className={cell}>{fmtDate(r.created_at)}</td>
                  <td className={`${cell} text-right`}>
                    <DeleteButton rowKey={r._key} onDelete={onDelete} testid={`admin-delete-speakers-${i}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Table({ tab, rows, onDelete, onReorder, onAttachPhoto, auth, onRefresh }) {
  if (tab === 'speakers') {
    return (
      <SpeakersTable
        rows={rows}
        onDelete={onDelete}
        onReorder={onReorder}
        onAttachPhoto={onAttachPhoto}
        auth={auth}
        onRefresh={onRefresh}
      />
    );
  }

  if (!rows.length) {
    return (
      <p data-testid={`admin-empty-${tab}`} className="rounded-xl border border-line/10 bg-ink-2/50 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
        No entries yet
      </p>
    );
  }

  const actionHead = <th className={`${head} text-right`}>Actions</th>;
  const actionCell = (key, i) => (
    <td className={`${cell} text-right`}>
      <DeleteButton rowKey={key} onDelete={onDelete} testid={`admin-delete-${tab}-${i}`} />
    </td>
  );

  if (tab === 'files') {
    return (
      <div className="overflow-x-auto rounded-xl border border-line/10" data-testid="admin-table-files">
        <table className="w-full min-w-[720px]">
          <thead className="bg-ink-3/60"><tr><th className={head}>File</th><th className={head}>Type</th><th className={head}>Size</th><th className={head}>Uploaded</th><th className={head}>Link</th>{actionHead}</tr></thead>
          <tbody className="divide-y divide-line/5 bg-ink-2/40">
            {rows.map((r, i) => (
              <tr key={r.id} className="transition-colors hover:bg-lime/5">
                <td className={cell}>
                  <span className="inline-flex items-center gap-2">
                    {(r.content_type || '').startsWith('image/') ? <ImageIcon size={14} className="text-lime" /> : <FileText size={14} className="text-lime" />}
                    {r.original_filename}
                  </span>
                </td>
                <td className={`${cell} font-mono text-[11px]`}>{r.content_type}</td>
                <td className={cell}>{fmtSize(r.size || 0)}</td>
                <td className={cell}>{fmtDate(r.created_at)}</td>
                <td className={cell}><FileLink path={r.storage_path} label={r.id} /></td>
                {actionCell(r.id, i)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const isPartner = tab === 'partners';
  return (
    <div className="overflow-x-auto rounded-xl border border-line/10" data-testid={`admin-table-${tab}`}>
      <table className="w-full min-w-[860px]">
        <thead className="bg-ink-3/60">
          <tr>
            <th className={head}>Name</th>
            {!isPartner && tab === 'registrations' && <th className={head}>Intent</th>}
            <th className={head}>{isPartner ? 'Company' : 'Organisation'}</th>
            <th className={head}>Designation</th>
            {tab === 'registrations' && <th className={head}>Event City</th>}
            <th className={head}>Email</th>
            <th className={head}>Mobile</th>
            {tab === 'registrations' && <th className={head}>City</th>}
            {isPartner && <th className={head}>Message</th>}
            <th className={head}>{isPartner ? 'Document' : 'Photo'}</th>
            <th className={head}>Submitted</th>
            {actionHead}
          </tr>
        </thead>
        <tbody className="divide-y divide-line/5 bg-ink-2/40">
          {rows.map((r, i) => (
            <tr key={r._key || i} className="transition-colors hover:bg-lime/5">
              <td className={`${cell} font-semibold text-slate-100`}>{r.first_name} {r.last_name}</td>
              {!isPartner && tab === 'registrations' && <td className={`${cell} font-mono text-[11px] text-lime`}>{r.intent}</td>}
              <td className={cell}>{isPartner ? r.company : r.organisation}</td>
              <td className={cell}>{r.designation}</td>
              {tab === 'registrations' && <td className={`${cell} font-semibold text-lime`}>{r.event_city || '—'}</td>}
              <td className={cell}><a href={`mailto:${r.email}`} className="hover:text-lime">{r.email}</a></td>
              <td className={cell}><a href={`tel:${(r.mobile || '').replace(/\s+/g, '')}`} className="hover:text-lime">{r.mobile || '—'}</a></td>
              {tab === 'registrations' && <td className={cell}>{r.city}</td>}
              {isPartner && <td className={`${cell} max-w-[220px] truncate`} title={r.message}>{r.message}</td>}
              <td className={cell}><FileLink path={isPartner ? r.doc_path : r.photo_path} label={`${tab}-${i}`} /></td>
              <td className={cell}>{fmtDate(r.created_at)}</td>
              {actionCell(r._key, i)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('registrations');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const auth = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } }),
    []
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    axios.post(`${API}/api/auth/logout`).catch(() => {});
    setUser(null);
    setRows([]);
    setStats(null);
  }, []);

  const loadStats = useCallback(() => {
    axios.get(`${API}/api/admin/overview`, auth()).then((res) => setStats(res.data)).catch(() => {});
  }, [auth]);

  const loadRows = useCallback(
    (t) => {
      const tabDef = TABS.find((x) => x.id === t);
      if (!tabDef || tabDef.content) return;
      setLoading(true);
      axios
        .get(`${API}${tabDef.endpoint}`, auth())
        .then((res) => setRows(res.data))
        .catch((err) => {
          if (err?.response?.status === 401) logout();
        })
        .finally(() => setLoading(false));
    },
    [auth, logout]
  );

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/api/auth/me`, auth())
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, [auth]);

  useEffect(() => {
    if (!user) return;
    loadStats();
  }, [user, loadStats]);

  useEffect(() => {
    if (!user) return;
    loadRows(tab);
  }, [user, tab, loadRows]);

  const deleteRow = async (key) => {
    try {
      await axios.delete(`${API}/api/admin/${tab}/${key}`, auth());
      loadRows(tab);
      loadStats();
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const reorderSpeakers = async (next) => {
    setRows(next);
    try {
      await axios.patch(`${API}/api/admin/speakers/reorder`, { keys: next.map((r) => r._key) }, auth());
      toast.success('Speaker order saved');
    } catch (err) {
      if (err?.response?.status === 401) return logout();
      toast.error('Could not save order');
      loadRows('speakers');
    }
  };

  const attachSpeakerPhoto = async (key, photoPath) => {
    await axios.patch(`${API}/api/admin/speakers/${key}/photo`, { photo_path: photoPath }, auth());
    loadRows('speakers');
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/admin/export/${tab}`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cxo100-2027-${tab}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setExporting(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
        Loading…
      </div>
    );
  }

  if (!user) return <Login onLogin={setUser} />;

  const ContentPanel = TABS.find((t) => t.id === tab)?.content;

  return (
    <div className="min-h-screen bg-ink pb-20 text-slate-100" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 border-b border-line/10">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Reticle className="h-8 w-8" />
            <div>
              <p className="font-display text-base font-extrabold tracking-tight">
                CXO <span className="text-lime">100</span> <span className="text-stroke-white">ADMIN</span>
              </p>
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-view-site-link"
              className="rounded-full border border-line/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-all hover:border-lime/60 hover:text-lime"
            >
              View Site
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-lime px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-all hover:shadow-[0_0_24px_rgba(153,204,0,0.4)]"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-5 pt-10">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          {[
            ['Registrations', stats?.registrations],
            ['Winners', stats?.winners],
            ['Partners', stats?.partners],
            ['Uploads', stats?.files],
            ['Speakers', stats?.speakers],
          ].map(([label, val]) => (
            <div key={label} data-testid={`admin-stat-${label.toLowerCase()}`} className="rounded-xl border border-line/10 bg-ink-2/60 p-5">
              <p className="font-display text-3xl font-extrabold tracking-tight">{val ?? '—'}</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => {
                  setRows([]);
                  setTab(t.id);
                }}
                className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-all ${
                  tab === t.id
                    ? 'bg-lime text-ink shadow-[0_0_24px_rgba(153,204,0,0.35)]'
                    : 'border border-line/10 text-slate-400 hover:border-lime/50 hover:text-lime'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {!ContentPanel && (
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              disabled={exporting || !rows.length}
              className="inline-flex items-center gap-2 rounded-full border border-lime/50 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-lime transition-all hover:bg-lime/10 hover:shadow-[0_0_24px_rgba(153,204,0,0.25)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Download size={13} /> {exporting ? 'Preparing…' : 'Export CSV'}
            </button>
          )}
        </div>

        {tab === 'speakers' && (
          <div className="mt-6">
            <SpeakerForm auth={auth} onAdded={() => { loadRows('speakers'); loadStats(); }} />
          </div>
        )}

        <div className="mt-6">
          {ContentPanel ? (
            <ContentPanel key={tab} auth={auth} onUnauthorized={logout} />
          ) : loading ? (
            <p className="rounded-xl border border-line/10 bg-ink-2/50 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
              Loading…
            </p>
          ) : (
            <Table
              tab={tab}
              rows={rows}
              onDelete={deleteRow}
              onReorder={reorderSpeakers}
              onAttachPhoto={attachSpeakerPhoto}
              auth={auth}
              onRefresh={() => { loadRows('speakers'); loadStats(); }}
            />
          )}
        </div>
      </main>
    </div>
  );
}
